import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getBackendUrl } from '../../src/config/config.loader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from '../ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Alert, AlertDescription } from '../ui/alert';
import { organizationService, Organization } from '../../src/services/organization.service';
import { authService, UserProfile } from '../../src/services/auth.service';
import { userService } from '../../src/services/user.service';
import { httpClient } from '../../src/services/http.client';
import { usePermission, ALL_ROLES, UserRole } from '../../src/utils/permissions';
import { useAuth } from '../../src/hooks/useAuth';
import { useOrgUsers } from '../../src/hooks/queries/use-org-users';
import { queryKeys } from '../../src/lib/query-keys';
import { showSuccess, showError } from '../../src/utils/notification.utils';
import { useBillingStatus, usePlans } from '../../src/hooks/useBilling';
import { BillingCheckout } from '../BillingCheckout';
import { CURRENCIES, setCachedCurrency } from '../../src/utils/currency';
import { TIMEZONES } from '../../src/utils/timezones';
import { NotificationChannel, getDefaultChannels, setDefaultChannels } from '../../src/services/notification.service';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { 
  Building2,
  Users,
  Plus,
  Edit,
  Trash2,
  Shield,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Settings as SettingsIcon,
  Bell,
  Lock,
  Eye,
  EyeOff,
  Download,
  Calendar,
  Check,
  Smartphone,
  Wallet,
  AlertTriangle,
  AlertCircle,
  User,
  Briefcase,
  UserCheck,
  MoreVertical,
  UserX,
  Activity,
  RefreshCw,
  LogIn,
  Zap,
  ChevronsUpDown,
  Loader2,
  CheckCircle,
  KeyRound,
} from 'lucide-react';

function BillingTabContent() {
  const { data: billingStatus, isLoading } = useBillingStatus();
  const { data: plans = [] } = usePlans();
  const [showCheckout, setShowCheckout] = useState(false);

  const plan = billingStatus?.plan;
  const statusLabel: Record<string, string> = {
    trialing: 'Trial',
    active: 'Active',
    expired: 'Expired',
    past_due: 'Past Due',
  };
  const statusVariant: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    trialing: 'secondary',
    active: 'default',
    expired: 'destructive',
    past_due: 'destructive',
  };

  const paidPlans = plans.filter((p) => p.tier !== 'FREE');

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Current Subscription
          </CardTitle>
          <CardDescription>Your current plan and billing information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="h-20 rounded-lg bg-muted animate-pulse" />
          ) : billingStatus ? (
            <>
              <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg">
                <div>
                  <h3 className="font-semibold">{plan?.display_name ?? 'Free Trial'}</h3>
                  {plan ? (
                    <p className="text-sm text-muted-foreground">
                      {plan.currency} {plan.price_monthly.toLocaleString()} / month
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">No active subscription</p>
                  )}
                  {billingStatus.is_trial && billingStatus.trial_days_remaining != null && (
                    <p className="text-sm text-amber-600 font-medium">
                      {billingStatus.trial_days_remaining} day{billingStatus.trial_days_remaining === 1 ? '' : 's'} remaining in trial
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <Badge variant={statusVariant[billingStatus.status] ?? 'secondary'}>
                    {statusLabel[billingStatus.status] ?? billingStatus.status}
                  </Badge>
                  {billingStatus.subscription_expires_at && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Expires {new Date(billingStatus.subscription_expires_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>

              {(billingStatus.status !== 'active' || billingStatus.is_trial) && (
                <div className="flex gap-2">
                  <Button onClick={() => setShowCheckout(true)} className="gap-2">
                    <Zap className="h-4 w-4" />
                    {billingStatus.status === 'expired' ? 'Reactivate Subscription' : 'Upgrade Plan'}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Unable to load billing information.</p>
          )}
        </CardContent>
      </Card>

      {paidPlans.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Available Plans</CardTitle>
            <CardDescription>Choose the plan that fits your team</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {paidPlans.map((p) => (
                <div key={p.plan_id} className="rounded-lg border p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">{p.display_name}</h4>
                    <span className="text-sm font-medium">
                      {p.currency} {p.price_monthly.toLocaleString()}/mo
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{p.description}</p>
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    <li>Up to {p.max_users === -1 ? 'unlimited' : p.max_users} users</li>
                    <li>Up to {p.max_projects === -1 ? 'unlimited' : p.max_projects} projects</li>
                    <li>{p.storage_limit_mb >= 1024 ? `${p.storage_limit_mb / 1024} GB` : `${p.storage_limit_mb} MB`} storage</li>
                  </ul>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <BillingCheckout open={showCheckout} onClose={() => setShowCheckout(false)} />
    </>
  );
}

// Enhanced User Form Component (for Add/Edit/View)
interface UserFormProps {
  mode: 'add' | 'edit' | 'view';
  user?: any;
  trigger?: React.ReactNode;
  orgId?: string;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function UserFormModal({ mode, user, trigger, orgId, onSuccess, open: controlledOpen, onOpenChange }: UserFormProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const handleOpenChange = (val: boolean) => {
    if (onOpenChange) onOpenChange(val);
    else setInternalOpen(val);
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    department: '',
    roles: [] as string[],
    hireDate: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    isActive: true,
    sendInvite: true,
    accessToSystem: false,
  });

  useEffect(() => {
    if (open) {
      setFormData({
        firstName: user?.first_name || '',
        lastName: user?.last_name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        jobTitle: user?.job_title || '',
        department: user?.department || '',
        roles: mode === 'add'
          ? []
          : (Array.isArray(user?.roles) && user.roles.length > 0
              ? user.roles
              : user?.role ? [user.role] : ['viewer']),
        hireDate: user?.hire_date || '',
        emergencyContactName: user?.emergency_contact_name || '',
        emergencyContactPhone: user?.emergency_contact_phone || '',
        isActive: user?.is_active !== undefined ? user.is_active : true,
        sendInvite: true,
        accessToSystem: false,
      });
    }
  }, [open, user]);

  const set = (field: string) => (val: any) =>
    setFormData((prev) => ({ ...prev, [field]: val }));

  const handleSubmit = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
      showError('First name, last name, and email are required');
      return;
    }
    if (formData.roles.length === 0) {
      showError('At least one role must be selected');
      return;
    }
    setIsSubmitting(true);
    try {
      if (mode === 'add') {
        if (!orgId) throw new Error('Organization not available');
        await userService.createUser({
          username: formData.email,
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          job_title: formData.jobTitle || undefined,
          department: formData.department || undefined,
          roles: formData.roles,
          organization_id: orgId,
          send_invite: formData.sendInvite,
          grant_system_access: formData.accessToSystem,
        });
        showSuccess({
          title: 'User created',
          message: formData.sendInvite ? 'Invitation email sent to the new user.' : undefined,
        });
      } else if (mode === 'edit') {
        await userService.updateUserProfile(user.user_id, {
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone || undefined,
          job_title: formData.jobTitle || undefined,
          department: formData.department || undefined,
          hire_date: formData.hireDate || undefined,
          emergency_contact_name: formData.emergencyContactName || undefined,
          emergency_contact_phone: formData.emergencyContactPhone || undefined,
          is_active: formData.isActive,
        });
        const existingRoles = Array.isArray(user.roles) ? user.roles : [user.role];
        if (formData.roles.join(',') !== existingRoles.join(',')) {
          await httpClient.patch(`/api/v1/users/${user.user_id}/roles`, {
            primary_role: formData.roles[0],
            additional_roles: formData.roles.slice(1),
          });
        }
        showSuccess('User updated successfully');
      }
      handleOpenChange(false);
      onSuccess?.();
    } catch (err: any) {
      showError({ title: 'Operation failed', message: err.message || 'Please try again' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isReadOnly = mode === 'view';
  const getTitle = () =>
    mode === 'add' ? 'Add New User' : mode === 'edit' ? 'Edit User' : 'View User Details';
  const getDesc = () =>
    mode === 'add' ? 'Create a new user account for your organization'
    : mode === 'edit' ? 'Update user account information'
    : 'View complete user account details';
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'add' && <Plus className="h-5 w-5" />}
            {mode === 'edit' && <Edit className="h-5 w-5" />}
            {mode === 'view' && <Eye className="h-5 w-5" />}
            {getTitle()}
          </DialogTitle>
          <DialogDescription>{getDesc()}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Personal Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Personal Information</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first-name">First Name <span className="text-destructive">*</span></Label>
                <Input
                  id="first-name"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={(e) => set('firstName')(e.target.value)}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last-name">Last Name <span className="text-destructive">*</span></Label>
                <Input
                  id="last-name"
                  placeholder="Smith"
                  value={formData.lastName}
                  onChange={(e) => set('lastName')(e.target.value)}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address <span className="text-destructive">*</span></Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john@company.com"
                  value={formData.email}
                  onChange={(e) => set('email')(e.target.value)}
                  readOnly={isReadOnly || mode === 'edit'}
                  className={isReadOnly || mode === 'edit' ? 'bg-muted' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1-555-0123"
                  value={formData.phone}
                  onChange={(e) => set('phone')(e.target.value)}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
            </div>
          </div>

          {/* Job Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Job Information</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="job-title">Job Title</Label>
                <Input
                  id="job-title"
                  placeholder="Site Engineer"
                  value={formData.jobTitle}
                  onChange={(e) => set('jobTitle')(e.target.value)}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Select value={formData.department} onValueChange={set('department')} disabled={isReadOnly}>
                  <SelectTrigger className={isReadOnly ? 'bg-muted' : ''}>
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="engineering">Engineering</SelectItem>
                    <SelectItem value="project-management">Project Management</SelectItem>
                    <SelectItem value="operations">Operations</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="hr">Human Resources</SelectItem>
                    <SelectItem value="safety">Safety & Quality</SelectItem>
                    <SelectItem value="procurement">Procurement</SelectItem>
                    <SelectItem value="administration">Administration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Roles <span className="text-destructive">*</span></Label>
                <div className={`grid grid-cols-2 gap-2 p-3 border rounded-md ${isReadOnly ? 'bg-muted' : ''}`}>
                  {ALL_ROLES.map((r) => (
                    <label key={r} className={`flex items-center gap-2 text-sm cursor-pointer ${isReadOnly ? 'cursor-default' : ''}`}>
                      <input
                        type="checkbox"
                        className="accent-primary"
                        checked={formData.roles.includes(r)}
                        disabled={isReadOnly}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          setFormData((prev) => ({
                            ...prev,
                            roles: e.target.checked
                              ? [...prev.roles, r]
                              : prev.roles.filter((x) => x !== r),
                          }));
                        }}
                      />
                      {r.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="hire-date">Hire Date</Label>
                <Input
                  id="hire-date"
                  type="date"
                  value={formData.hireDate}
                  onChange={(e) => set('hireDate')(e.target.value)}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Emergency Contact</h4>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emergency-name">Contact Name</Label>
                <Input
                  id="emergency-name"
                  placeholder="Jane Smith"
                  value={formData.emergencyContactName}
                  onChange={(e) => set('emergencyContactName')(e.target.value)}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency-phone">Contact Phone</Label>
                <Input
                  id="emergency-phone"
                  type="tel"
                  placeholder="+1-555-0456"
                  value={formData.emergencyContactPhone}
                  onChange={(e) => set('emergencyContactPhone')(e.target.value)}
                  readOnly={isReadOnly}
                  className={isReadOnly ? 'bg-muted' : ''}
                />
              </div>
            </div>
          </div>

          {/* Account Settings */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-primary" />
              <h4 className="font-medium">Account Settings</h4>
            </div>
            {mode !== 'add' && (
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <Label htmlFor="active-user">Active Account</Label>
                  <p className="text-sm text-muted-foreground">User can access the system</p>
                </div>
                <Switch
                  id="active-user"
                  checked={formData.isActive}
                  onCheckedChange={set('isActive')}
                  disabled={isReadOnly}
                />
              </div>
            )}
            {mode === 'add' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label htmlFor="access-to-system">Access to system</Label>
                    <p className="text-sm text-muted-foreground">User will be invited to log in to OptiBuild</p>
                  </div>
                  <Switch
                    id="access-to-system"
                    checked={formData.accessToSystem}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setShowAccessDialog(true);
                      } else {
                        set('accessToSystem')(false);
                      }
                    }}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="send-invite"
                    checked={formData.sendInvite}
                    onCheckedChange={set('sendInvite')}
                  />
                  <Label htmlFor="send-invite">Send invitation email to new user</Label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Access to system confirmation dialog */}
        <AlertDialog open={showAccessDialog} onOpenChange={setShowAccessDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <LogIn className="h-5 w-5 text-primary" />
                System access enabled
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm leading-relaxed">
                Access to system is checked — this user will be invited to access OptiBuild and will be
                counted as an active user. Your next licence renewal will be calculated based on the
                number of active users at that time.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => {
                  set('accessToSystem')(false);
                  setShowAccessDialog(false);
                }}
              >
                Review
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  set('accessToSystem')(true);
                  setShowAccessDialog(false);
                }}
              >
                Proceed
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
          {mode !== 'view' && (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              ) : mode === 'add' ? 'Add User' : 'Save Changes'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Delete / Deactivate User Modal
interface DeleteUserModalProps {
  user: any;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function DeleteUserModal({ user, trigger, onSuccess, open: controlledOpen, onOpenChange }: DeleteUserModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const handleOpenChange = (val: boolean) => {
    if (onOpenChange) onOpenChange(val);
    else setInternalOpen(val);
  };
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeactivate = async () => {
    setIsDeactivating(true);
    try {
      await httpClient.post(`/api/v1/users/${user.user_id}/deactivate`);
      showSuccess(`${user.first_name} ${user.last_name} has been deactivated`);
      handleOpenChange(false);
      onSuccess?.();
    } catch {
      showError('Failed to deactivate user');
    } finally {
      setIsDeactivating(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await userService.deleteUser(user.user_id);
      showSuccess('User account deleted');
      handleOpenChange(false);
      onSuccess?.();
    } catch {
      showError('Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-[500px] modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Remove User Account
          </DialogTitle>
          <DialogDescription>
            Choose to deactivate (preserves data) or permanently delete this user.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Removing <strong>{user?.first_name} {user?.last_name}</strong> ({user?.email}).
              <ul className="mt-2 space-y-1 text-sm">
                <li>• All project assignments will be removed</li>
                <li>• User will lose access immediately</li>
                <li>• Activity history is preserved for audit purposes</li>
              </ul>
            </AlertDescription>
          </Alert>
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Recommended:</strong> Deactivate to preserve data while revoking access.
            </p>
          </div>
        </div>
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancel</Button>
          <Button
            variant="outline"
            className="border-warning text-warning hover:bg-warning/10"
            onClick={handleDeactivate}
            disabled={isDeactivating || isDeleting}
          >
            <UserX className="h-4 w-4 mr-2" />
            {isDeactivating ? 'Deactivating...' : 'Deactivate'}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeactivating || isDeleting}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {isDeleting ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OrganizationProfile() {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [isSavingCurrency, setIsSavingCurrency] = useState(false);
  const [timezone, setTimezone] = useState('UTC');
  const [isSavingTimezone, setIsSavingTimezone] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const [notifChannels, setNotifChannels] = useState<Set<NotificationChannel>>(
    () => new Set(getDefaultChannels()),
  );

  const { data: billingStatus } = useBillingStatus();
  const planTier = billingStatus?.plan?.tier ?? 'FREE';
  const isBillingTrial = billingStatus?.is_trial ?? false;
  const canEditSecurity = planTier === 'PROFESSIONAL' || planTier === 'ENTERPRISE' || isBillingTrial;
  const canUseAutoBackup = planTier === 'ENTERPRISE';

  const [securitySettings, setSecuritySettings] = useState({
    two_factor_required: false,
    strong_password_policy: true,
    session_timeout: '4h' as string,
    audit_logging_enabled: true,
    automatic_backups: false,
  });
  const [isEditingSecurity, setIsEditingSecurity] = useState(false);
  const [isSavingSecurity, setIsSavingSecurity] = useState(false);
  const [showUpgradeCheckout, setShowUpgradeCheckout] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError('');

        if (!authService.isAuthenticated()) {
          throw new Error('Not authenticated');
        }

        const profileResponse = await authService.getUserProfile();
        setUserProfile(profileResponse);

        if (profileResponse.organization?.id) {
          try {
            const orgData = await organizationService.getOrganization(profileResponse.organization.id);
            setOrganization(orgData);
            if (orgData.currency) setCurrency(orgData.currency);
            if (orgData.timezone) setTimezone(orgData.timezone);
          } catch (orgError) {
            console.warn('Failed to fetch full organization details:', orgError);
            setOrganization(profileResponse.organization as Organization);
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch organization profile:', err);
        setError(err.message || 'Failed to load organization profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSaveChanges = async () => {
    setIsEditing(false);
  };

  const handleSaveCurrency = async () => {
    if (!organization?.id) return;
    setIsSavingCurrency(true);
    try {
      await organizationService.updateOrganization(organization.id, { currency });
      setOrganization((prev) => prev ? { ...prev, currency } : prev);
      setCachedCurrency(currency);
      showSuccess('Default currency updated');
    } catch {
      showError('Failed to save currency preference');
    } finally {
      setIsSavingCurrency(false);
    }
  };

  const handleSaveTimezone = async () => {
    if (!organization?.id) return;
    setIsSavingTimezone(true);
    try {
      await organizationService.updateOrganization(organization.id, { timezone });
      setOrganization((prev) => prev ? { ...prev, timezone } : prev);
      showSuccess('Default timezone updated');
    } catch {
      showError('Failed to save timezone preference');
    } finally {
      setIsSavingTimezone(false);
    }
  };

  const handleSaveSecuritySettings = async () => {
    if (!organization?.id) return;
    setIsSavingSecurity(true);
    try {
      await httpClient.patch(`/api/v1/organizations/${organization.id}/security`, securitySettings);
      setIsEditingSecurity(false);
      showSuccess('Security settings saved');
    } catch {
      showError('Failed to save security settings');
    } finally {
      setIsSavingSecurity(false);
    }
  };

  const handleSecurityUpgradePrompt = () => {
    showError({
      title: 'Upgrade Required',
      message: 'Security settings require a Professional or Enterprise plan.',
    });
  };

  const toggleChannel = (channel: NotificationChannel) => {
    setNotifChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channel)) {
        if (next.size === 1) return prev; // keep at least one enabled
        next.delete(channel);
      } else {
        next.add(channel);
      }
      const arr = Array.from(next) as NotificationChannel[];
      setDefaultChannels(arr);
      showSuccess('Notification channel preference saved');
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading organization profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Organization Profile</h2>
          <p className="text-muted-foreground">Manage your organization settings and preferences</p>
        </div>
        <Button onClick={() => setIsEditing(!isEditing)}>
          <Edit className="h-4 w-4 mr-2" />
          {isEditing ? 'Cancel' : 'Edit Profile'}
        </Button>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="billing">Billing & Subscription</TabsTrigger>
          <TabsTrigger value="preferences">Preferences</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>
        
        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Company Information
              </CardTitle>
              <CardDescription>Basic information about your construction company</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company-name">Company Name</Label>
                  <Input 
                    id="company-name" 
                    value={organization?.name || 'Not specified'} 
                    readOnly={!isEditing}
                    className={!isEditing ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company-code">Company Code</Label>
                  <Input 
                    id="company-code" 
                    value={organization?.companyCode || 'Not specified'} 
                    readOnly={!isEditing}
                    className={!isEditing ? 'bg-muted' : ''}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-person">Primary Contact</Label>
                  <Input 
                    id="contact-person" 
                    value={organization?.primaryContact || userProfile?.name || 'Not specified'} 
                    readOnly={!isEditing}
                    className={!isEditing ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-email">Contact Email</Label>
                  <Input 
                    id="contact-email" 
                    value={organization?.email || userProfile?.email || 'Not specified'} 
                    readOnly={!isEditing}
                    className={!isEditing ? 'bg-muted' : ''}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input 
                    id="phone" 
                    value={organization?.phone || 'Not specified'} 
                    readOnly={!isEditing}
                    className={!isEditing ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input 
                    id="website" 
                    value={organization?.website || 'Not specified'} 
                    readOnly={!isEditing}
                    className={!isEditing ? 'bg-muted' : ''}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address">Business Address</Label>
                <Textarea 
                  id="address" 
                  value={organization?.address || 'Not specified'} 
                  readOnly={!isEditing}
                  className={!isEditing ? 'bg-muted' : ''}
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country of Registration</Label>
                  <Input 
                    id="country" 
                    value={organization?.countryOfRegistration || 'Not specified'} 
                    readOnly={!isEditing}
                    className={!isEditing ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="registration-number">Registration Number</Label>
                  <Input 
                    id="registration-number" 
                    value={organization?.registrationNumber || 'Not specified'} 
                    readOnly={!isEditing}
                    className={!isEditing ? 'bg-muted' : ''}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="license">License Number</Label>
                  <Input 
                    id="license" 
                    value={organization?.licenseNumber || 'Not specified'} 
                    readOnly={!isEditing}
                    className={!isEditing ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tax-id">Tax ID</Label>
                  <Input 
                    id="tax-id" 
                    value={organization?.taxId || 'Not specified'} 
                    readOnly={!isEditing}
                    className={!isEditing ? 'bg-muted' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="organization-id">Organization ID</Label>
                  <Input 
                    id="organization-id" 
                    value={organization?.id || 'Not available'} 
                    readOnly 
                    className="bg-muted"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="created">Created</Label>
                  <Input 
                    id="created" 
                    value={organization?.createdAt ? new Date(organization.createdAt).toLocaleString() : 'Not available'} 
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last-updated">Last Updated</Label>
                  <Input 
                    id="last-updated" 
                    value={organization?.updatedAt ? new Date(organization.updatedAt).toLocaleString() : 'Not available'} 
                    readOnly 
                    className="bg-muted" 
                  />
                </div>
              </div>
              
              {isEditing && (
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveChanges}>
                    Save Changes
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Business Details</CardTitle>
              <CardDescription>Additional information about your construction business</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="industry">Primary Industry</Label>
                  <Select 
                    defaultValue={organization?.industry || "not-specified"} 
                    disabled={!isEditing}
                  >
                    <SelectTrigger className={!isEditing ? 'bg-muted' : ''}>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not-specified">Not Specified</SelectItem>
                      <SelectItem value="residential">Residential Construction</SelectItem>
                      <SelectItem value="commercial">Commercial Construction</SelectItem>
                      <SelectItem value="industrial">Industrial Construction</SelectItem>
                      <SelectItem value="infrastructure">Infrastructure</SelectItem>
                      <SelectItem value="civil">Civil Engineering</SelectItem>
                      <SelectItem value="renovation">Renovation & Remodeling</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="size">Company Size</Label>
                  <Input 
                    id="size" 
                    value="Not specified" 
                    readOnly 
                    className="bg-muted"
                    placeholder="Company size information not available"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="subscription-plan">Subscription Plan</Label>
                  <div className="flex items-center gap-2">
                    <Input 
                      id="subscription-plan" 
                      value={organization?.subscriptionPlan || 'Not specified'} 
                      readOnly 
                      className="bg-muted" 
                    />
                    <Badge variant={organization?.isActive ? "default" : "secondary"}>
                      {organization?.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-users">Max Users</Label>
                  <div className="flex items-center gap-2">
                    <Input 
                      id="max-users" 
                      value={organization?.maxUsers ? `${organization.maxUsers} users` : 'Not specified'} 
                      readOnly 
                      className="bg-muted" 
                    />
                    <Badge variant="secondary">Details</Badge>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="specialties">Specialties</Label>
                <Textarea 
                  id="specialties" 
                  value={organization?.specialties || 'Not specified'} 
                  readOnly={!isEditing}
                  className={!isEditing ? 'bg-muted' : ''}
                  rows={3}
                />
              </div>

              {/* User Profile Information */}
              {userProfile && (
                <div className="border-t pt-4 mt-6">
                  <h4 className="font-medium mb-3 text-primary">Current User Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="user-name">Full Name</Label>
                      <Input 
                        id="user-name" 
                        value={userProfile.name} 
                        readOnly 
                        className="bg-muted" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="user-email">Email</Label>
                      <Input 
                        id="user-email" 
                        value={userProfile.email} 
                        readOnly 
                        className="bg-muted" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="user-job-title">Job Title</Label>
                      <Input 
                        id="user-job-title" 
                        value={userProfile.jobTitle || 'Not specified'} 
                        readOnly 
                        className="bg-muted" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="user-roles">Roles</Label>
                      <div className="flex gap-1 flex-wrap">
                        {userProfile.roles.map((role, index) => (
                          <Badge key={index} variant="secondary">{role}</Badge>
                        ))}
                        {userProfile.roles.length === 0 && (
                          <Badge variant="outline">No roles assigned</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="billing" className="space-y-6">
          <BillingTabContent />
        </TabsContent>
        
        <TabsContent value="preferences" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SettingsIcon className="h-5 w-5" />
                General Preferences
              </CardTitle>
              <CardDescription>Configure default settings for your organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Default Timezone</Label>
                  <p className="text-sm text-muted-foreground">Used for scheduling and reporting</p>
                </div>
                <div className="flex items-center gap-2">
                  <Popover open={timezoneOpen} onOpenChange={setTimezoneOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-[300px] justify-between font-normal">
                        <span className="truncate">
                          {TIMEZONES.find(t => t.value === timezone)?.label ?? timezone}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[340px] p-0">
                      <Command>
                        <CommandInput placeholder="Search timezone or city..." />
                        <CommandList>
                          <CommandEmpty>No timezone found.</CommandEmpty>
                          <CommandGroup>
                            {TIMEZONES.map((tz) => (
                              <CommandItem
                                key={tz.value}
                                value={`${tz.label} ${tz.cities}`}
                                onSelect={() => { setTimezone(tz.value); setTimezoneOpen(false); }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${timezone === tz.value ? 'opacity-100' : 'opacity-0'}`} />
                                <div>
                                  <div className="text-sm">{tz.label}</div>
                                  <div className="text-xs text-muted-foreground">{tz.cities}</div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button size="sm" onClick={handleSaveTimezone} disabled={isSavingTimezone}>
                    {isSavingTimezone ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Default Currency</Label>
                  <p className="text-sm text-muted-foreground">Used for all financial calculations org-wide</p>
                </div>
                <div className="flex items-center gap-2">
                  <Popover open={currencyOpen} onOpenChange={setCurrencyOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-[300px] justify-between font-normal">
                        <span className="truncate">
                          {CURRENCIES.find(c => c.code === currency)?.label ?? currency}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[340px] p-0">
                      <Command>
                        <CommandInput placeholder="Search currency or country..." />
                        <CommandList>
                          <CommandEmpty>No currency found.</CommandEmpty>
                          <CommandGroup>
                            {CURRENCIES.map((c) => (
                              <CommandItem
                                key={c.code}
                                value={`${c.label} ${c.country}`}
                                onSelect={() => { setCurrency(c.code); setCurrencyOpen(false); }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${currency === c.code ? 'opacity-100' : 'opacity-0'}`} />
                                <div>
                                  <div className="text-sm">{c.label}</div>
                                  <div className="text-xs text-muted-foreground">{c.country}</div>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button size="sm" onClick={handleSaveCurrency} disabled={isSavingCurrency}>
                    {isSavingCurrency ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="date-format">Date Format</Label>
                  <p className="text-sm text-muted-foreground">How dates are displayed</p>
                </div>
                <Select defaultValue="mm-dd-yyyy">
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mm-dd-yyyy">MM/DD/YYYY</SelectItem>
                    <SelectItem value="dd-mm-yyyy">DD/MM/YYYY</SelectItem>
                    <SelectItem value="yyyy-mm-dd">YYYY-MM-DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Channels
              </CardTitle>
              <CardDescription>
                Choose which channels are used when sending notifications to users. At least one channel must remain enabled.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(
                [
                  { channel: 'EMAIL' as NotificationChannel, label: 'Email', description: 'Deliver notifications via email' },
                  { channel: 'SMS' as NotificationChannel, label: 'SMS', description: 'Send urgent notifications as text messages' },
                  { channel: 'PUSH' as NotificationChannel, label: 'Push Notifications', description: 'Browser and mobile push notifications' },
                  { channel: 'IN_APP' as NotificationChannel, label: 'In-App', description: 'Show notifications inside the OptiBuild app' },
                  { channel: 'SLACK' as NotificationChannel, label: 'Slack', description: 'Post notifications to a connected Slack workspace' },
                  { channel: 'TEAMS' as NotificationChannel, label: 'Microsoft Teams', description: 'Post notifications to a connected Teams channel' },
                  { channel: 'WEBHOOK' as NotificationChannel, label: 'Webhook', description: 'HTTP POST to a custom endpoint for integrations' },
                ] as const
              ).map(({ channel, label, description }) => (
                <div key={channel} className="flex items-center justify-between">
                  <div>
                    <Label htmlFor={`notif-${channel}`}>{label}</Label>
                    <p className="text-sm text-muted-foreground">{description}</p>
                  </div>
                  <Switch
                    id={`notif-${channel}`}
                    checked={notifChannels.has(channel)}
                    onCheckedChange={() => toggleChannel(channel)}
                  />
                </div>
              ))}
              <p className="text-xs text-muted-foreground pt-1">
                Changes take effect immediately for all future notifications.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="security" className="space-y-6">
          {!canEditSecurity && (
            <Alert>
              <Lock className="h-4 w-4" />
              <AlertDescription>
                Security settings require a <strong>Professional</strong> or <strong>Enterprise</strong> plan.{' '}
                <button
                  className="text-primary underline font-medium"
                  onClick={() => setShowUpgradeCheckout(true)}
                >
                  Upgrade now
                </button>{' '}
                to manage your organization&apos;s security policies.
              </AlertDescription>
            </Alert>
          )}

          <Card className={!canEditSecurity ? 'opacity-60' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="h-5 w-5" />
                    Security Settings
                  </CardTitle>
                  <CardDescription>Manage security policies for your organization</CardDescription>
                </div>
                {canEditSecurity && !isEditingSecurity && (
                  <Button variant="outline" size="sm" onClick={() => setIsEditingSecurity(true)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Two-Factor Authentication</Label>
                  <p className="text-sm text-muted-foreground">Require 2FA for all users</p>
                </div>
                <Switch
                  checked={securitySettings.two_factor_required}
                  onCheckedChange={(v) => {
                    if (!canEditSecurity) { handleSecurityUpgradePrompt(); return; }
                    if (isEditingSecurity) setSecuritySettings((s) => ({ ...s, two_factor_required: v }));
                  }}
                  disabled={!canEditSecurity || !isEditingSecurity}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Strong Password Policy</Label>
                  <p className="text-sm text-muted-foreground">Enforce complex password requirements</p>
                </div>
                <Switch
                  checked={securitySettings.strong_password_policy}
                  onCheckedChange={(v) => {
                    if (!canEditSecurity) { handleSecurityUpgradePrompt(); return; }
                    if (isEditingSecurity) setSecuritySettings((s) => ({ ...s, strong_password_policy: v }));
                  }}
                  disabled={!canEditSecurity || !isEditingSecurity}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Session Timeout</Label>
                  <p className="text-sm text-muted-foreground">Automatically log out inactive users</p>
                </div>
                <Select
                  value={securitySettings.session_timeout}
                  onValueChange={(v) => {
                    if (!canEditSecurity) { handleSecurityUpgradePrompt(); return; }
                    if (isEditingSecurity) setSecuritySettings((s) => ({ ...s, session_timeout: v }));
                  }}
                  disabled={!canEditSecurity || !isEditingSecurity}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1h">1 hour</SelectItem>
                    <SelectItem value="4h">4 hours</SelectItem>
                    <SelectItem value="8h">8 hours</SelectItem>
                    <SelectItem value="24h">24 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Audit Logging</Label>
                  <p className="text-sm text-muted-foreground">Log all user activities</p>
                </div>
                <Switch
                  checked={securitySettings.audit_logging_enabled}
                  onCheckedChange={(v) => {
                    if (!canEditSecurity) { handleSecurityUpgradePrompt(); return; }
                    if (isEditingSecurity) setSecuritySettings((s) => ({ ...s, audit_logging_enabled: v }));
                  }}
                  disabled={!canEditSecurity || !isEditingSecurity}
                />
              </div>

              {isEditingSecurity && (
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button variant="outline" onClick={() => setIsEditingSecurity(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveSecuritySettings} disabled={isSavingSecurity}>
                    {isSavingSecurity ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className={!canEditSecurity ? 'opacity-60' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Data Backup &amp; Recovery
              </CardTitle>
              <CardDescription>Backup settings and data recovery options</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <Label>Automatic Backups</Label>
                    {!canUseAutoBackup && (
                      <Badge variant="outline" className="text-xs">Enterprise only</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">Daily automatic data backups</p>
                </div>
                <Switch
                  checked={securitySettings.automatic_backups}
                  onCheckedChange={(v) => {
                    if (!canUseAutoBackup) {
                      showError({ title: 'Enterprise Feature', message: 'Automatic backups are available on Enterprise plan only.' });
                      return;
                    }
                    if (isEditingSecurity) setSecuritySettings((s) => ({ ...s, automatic_backups: v }));
                  }}
                  disabled={!canUseAutoBackup || !isEditingSecurity}
                />
              </div>

              <div>
                <Label>Last Backup</Label>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {canUseAutoBackup && securitySettings.automatic_backups
                    ? 'Automatic backups are enabled'
                    : 'No automatic backups configured'}
                </p>
                {canUseAutoBackup && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    disabled={!securitySettings.automatic_backups}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Backup
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <BillingCheckout open={showUpgradeCheckout} onClose={() => setShowUpgradeCheckout(false)} />
    </div>
  );
}

// --- Module-level role capability display data ---
const ROLE_MODULES: Record<string, { badge: string; description: string; canView: string[]; canWrite: string[] }> = {
  admin: {
    badge: 'Full Access',
    description: 'Complete system access with all permissions including user management and system configuration.',
    canView: ['Projects', 'Financials', 'User Management', 'Settings', 'Reports', 'Procurement', 'Documents', 'Audit & Compliance'],
    canWrite: ['Projects', 'Financials', 'User Management', 'Settings', 'Reports', 'Procurement', 'Documents'],
  },
  'project-manager': {
    badge: 'Project Level',
    description: 'Manage assigned projects with access to scheduling, budgets, and team coordination.',
    canView: ['Projects', 'Schedules', 'Budgets', 'Documents', 'RFIs', 'Submittals', 'Reports'],
    canWrite: ['Projects', 'Schedules', 'Daily Logs', 'Documents', 'RFIs', 'Submittals'],
  },
  estimator: {
    badge: 'Pre-Construction',
    description: 'Pre-construction planning including BOQ, estimates, bids, and proposals.',
    canView: ['BOQ', 'Estimates', 'Bids', 'Resources', 'Reports'],
    canWrite: ['BOQ', 'Estimates', 'Bids', 'Resources'],
  },
  'site-engineer': {
    badge: 'Field Access',
    description: 'Field-focused access for daily operations, quality control, and site documentation.',
    canView: ['Daily Logs', 'Schedules', 'Inspections', 'Safety', 'Documents'],
    canWrite: ['Daily Logs', 'Safety Reports', 'Material Requests'],
  },
  'site-supervisor': {
    badge: 'Site Management',
    description: 'Site management with team coordination, safety oversight, and progress tracking.',
    canView: ['Daily Logs', 'Schedules', 'Safety', 'Inspections', 'Punch Lists', 'Documents'],
    canWrite: ['Daily Logs', 'Safety', 'Inspections', 'Goods Received'],
  },
  'site-worker': {
    badge: 'Basic Field',
    description: 'Basic field access for daily logs, safety reporting, and schedule viewing.',
    canView: ['Daily Logs', 'Schedules', 'Safety'],
    canWrite: ['Daily Logs', 'Safety Reports'],
  },
  'procurement-officer': {
    badge: 'Procurement',
    description: 'Full procurement workflow including suppliers, purchase orders, and inventory.',
    canView: ['Suppliers', 'Purchase Orders', 'Goods Received', 'Inventory', 'Material Requisitions'],
    canWrite: ['Suppliers', 'Purchase Orders', 'Goods Received', 'Inventory', 'Material Requisitions'],
  },
  'finance-manager': {
    badge: 'Financial Full',
    description: 'Full financial management including budgets, contracts, invoices, and payments.',
    canView: ['Budgets', 'Contracts', 'Invoices', 'Payments', 'Cost Codes', 'Timesheets', 'Change Orders', 'Reports'],
    canWrite: ['Budgets', 'Contracts', 'Invoices', 'Payments', 'Cost Codes', 'Change Orders'],
  },
  accountant: {
    badge: 'Financial View',
    description: 'Financial record management with invoice processing and reporting.',
    canView: ['Budgets', 'Contracts', 'Invoices', 'Payment Applications', 'Cost Codes', 'Reports'],
    canWrite: ['Invoices', 'Payment Applications'],
  },
  'safety-officer': {
    badge: 'Safety',
    description: 'Safety management including incidents, inspections, and compliance reporting.',
    canView: ['Safety Incidents', 'Inspections', 'Daily Logs', 'Punch Lists', 'Documents'],
    canWrite: ['Safety Incidents', 'Inspections', 'Punch Lists'],
  },
  viewer: {
    badge: 'Read Only',
    description: 'Read-only access to view project information and reports.',
    canView: ['Dashboard', 'Projects', 'Documents', 'Reports'],
    canWrite: [],
  },
};

export function UserManagement() {
  const { isAdmin } = usePermission();
  const { orgId } = useAuth();
  const [roleAssignLoading, setRoleAssignLoading] = useState(false);
  const [viewingUser, setViewingUser] = useState<any | null>(null);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [deletingUser, setDeletingUser] = useState<any | null>(null);

  const { data: rawUsers = [], isLoading: loading, isError, refetch: refetchUsers } = useOrgUsers(orgId);
  const users = (rawUsers as any[]).map((u: any) => ({
    user_id: u.user_id,
    first_name: u.first_name || '',
    last_name: u.last_name || '',
    email: u.email,
    roles: Array.isArray(u.roles) && u.roles.length > 0 ? u.roles : [u.role || 'viewer'],
    role: (Array.isArray(u.roles) && u.roles[0]) || u.role || 'viewer',
    is_active: u.is_active !== false,
    email_verified: u.email_verified || false,
    last_login: u.last_login,
    project_count: u.project_count || 0,
    created_at: u.created_at,
    phone: u.phone || '',
    job_title: u.job_title || '',
    department: u.department || '',
    hire_date: u.hire_date || '',
    emergency_contact_name: u.emergency_contact_name || '',
    emergency_contact_phone: u.emergency_contact_phone || '',
  }));

  const { data: activityLogs = [], isLoading: logsLoading } = useQuery({
    queryKey: queryKeys.audit.activity(orgId ?? ''),
    queryFn: () => httpClient.get('/api/v1/audit/logs?size=30'),
    select: (res: any) => res.data?.items ?? (Array.isArray(res.data) ? res.data : []),
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const handleAssignRole = async (userId: string, newRole: UserRole) => {
    setRoleAssignLoading(true);
    try {
      await httpClient.patch(`/api/v1/users/${userId}/roles`, {
        primary_role: newRole,
        additional_roles: [],
      });
      showSuccess('Role updated');
      refetchUsers();
    } catch {
      showError('Failed to update role');
    } finally {
      setRoleAssignLoading(false);
    }
  };

  const totalUsers = users.length;
  const activeUsers = users.filter((u) => u.is_active).length;
  const pendingInvites = users.filter((u) => u.is_active && !u.email_verified).length;
  const adminUsers = users.filter((u) => u.roles?.includes('admin') || u.role === 'admin').length;
  const error = isError ? 'Failed to load users' : '';

  const logColor = (action: string) => {
    if (action === 'create') return 'border-l-green-500 bg-green-50 dark:bg-green-950/20';
    if (action === 'delete') return 'border-l-red-500 bg-red-50 dark:bg-red-950/20';
    if (action === 'assign') return 'border-l-blue-500 bg-blue-50 dark:bg-blue-950/20';
    return 'border-l-orange-500 bg-orange-50 dark:bg-orange-950/20';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">User Management</h2>
          <p className="text-muted-foreground">Manage users, roles, and permissions</p>
        </div>
        {isAdmin && (
          <UserFormModal
            mode="add"
            orgId={orgId ?? ''}
            onSuccess={() => refetchUsers()}
            trigger={
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            }
          />
        )}
      </div>

      {/* Stats cards — derived from live user data */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '—' : totalUsers}</div>
            <p className="text-xs text-muted-foreground">In this organisation</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '—' : activeUsers}</div>
            <p className="text-xs text-muted-foreground">
              {loading ? '' : `${totalUsers > 0 ? Math.round((activeUsers / totalUsers) * 100) : 0}% of users`}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending Invites</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '—' : pendingInvites}</div>
            <p className="text-xs text-muted-foreground">Awaiting email verification</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Admin Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{loading ? '—' : adminUsers}</div>
            <p className="text-xs text-muted-foreground">
              {loading ? '' : `${totalUsers > 0 ? Math.round((adminUsers / totalUsers) * 100) : 0}% of users`}
            </p>
          </CardContent>
        </Card>
      </div>

      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading users...</p>
        </div>
      )}

      {error && (
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-12 w-12 text-destructive mb-4" />
          <h3 className="text-lg font-medium mb-2">Failed to load users</h3>
          <p className="text-muted-foreground">{error}</p>
          <Button variant="outline" className="mt-4" onClick={() => refetchUsers()}>
            <RefreshCw className="h-4 w-4 mr-2" />Retry
          </Button>
        </div>
      )}

      {!loading && !error && (
        <Tabs defaultValue="users" className="w-full">
          <TabsList>
            <TabsTrigger value="users">All Users</TabsTrigger>
            <TabsTrigger value="roles">Roles & Permissions</TabsTrigger>
            <TabsTrigger value="activity">Activity Log</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Team Members
                </CardTitle>
                <CardDescription>Manage user accounts and access levels</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Projects</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead>Join Date</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.user_id}>
                        <TableCell className="font-medium">
                          {user.first_name} {user.last_name}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(user.roles || [user.role]).map((r: string) => (
                              <Badge key={r} variant={r === 'admin' ? 'default' : 'secondary'} className="text-xs">
                                {r.replace(/-/g, ' ')}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.is_active ? 'default' : 'secondary'}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>{user.project_count}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {user.last_login ? new Date(user.last_login).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => setViewingUser(user)}>
                                <Eye className="h-4 w-4 mr-2" />View Details
                              </DropdownMenuItem>
                              {isAdmin && (
                                <>
                                  <DropdownMenuItem onSelect={() => setEditingUser(user)}>
                                    <Edit className="h-4 w-4 mr-2" />Edit User
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onSelect={() => setDeletingUser(user)}>
                                    <UserX className="h-4 w-4 mr-2" />
                                    {user.is_active ? 'Deactivate / Delete' : 'Activate / Delete'}
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          No users found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="roles">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Roles & Permissions
                </CardTitle>
                <CardDescription>Reference guide for each role's access level</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {ALL_ROLES.map((role) => {
                    const info = ROLE_MODULES[role];
                    if (!info) return null;
                    return (
                      <div key={role} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold capitalize">{role.replace(/-/g, ' ')}</h3>
                          <Badge variant={role === 'admin' ? 'default' : 'secondary'}>{info.badge}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{info.description}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="font-medium text-xs text-muted-foreground uppercase mb-1">Can View</p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1">
                              {info.canView.map((area) => (
                                <span key={area} className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
                                  <Check className="h-3 w-3" />{area}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="font-medium text-xs text-muted-foreground uppercase mb-1">Can Write</p>
                            {info.canWrite.length > 0 ? (
                              <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {info.canWrite.map((area) => (
                                  <span key={area} className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-400">
                                    <Edit className="h-3 w-3" />{area}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">No write access</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" />
                  Activity Log
                </CardTitle>
                <CardDescription>User management events and system actions</CardDescription>
              </CardHeader>
              <CardContent>
                {logsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
                    <p className="text-muted-foreground text-sm">Loading activity...</p>
                  </div>
                ) : activityLogs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Activity className="mx-auto h-10 w-10 mb-3 opacity-30" />
                    <p>No activity recorded yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activityLogs.map((log: any, idx: number) => (
                      <div key={log.log_id || idx} className={`flex items-start gap-3 p-3 border-l-4 rounded-r ${logColor(log.action)}`}>
                        <div className="flex-1">
                          <p className="text-sm font-medium capitalize">{log.action?.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-muted-foreground">
                            {log.description || `${log.resource_type || ''} — ${log.status || ''}`}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {log.created_at ? new Date(log.created_at).toLocaleString() : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Controlled modals rendered outside dropdown to avoid Radix context hierarchy conflict */}
      {viewingUser && (
        <UserFormModal
          mode="view"
          user={viewingUser}
          open={true}
          onOpenChange={(o) => { if (!o) setViewingUser(null); }}
        />
      )}
      {editingUser && (
        <UserFormModal
          mode="edit"
          user={editingUser}
          orgId={orgId ?? ''}
          onSuccess={() => { refetchUsers(); setEditingUser(null); }}
          open={true}
          onOpenChange={(o) => { if (!o) setEditingUser(null); }}
        />
      )}
      {deletingUser && (
        <DeleteUserModal
          user={deletingUser}
          onSuccess={() => { refetchUsers(); setDeletingUser(null); }}
          open={true}
          onOpenChange={(o) => { if (!o) setDeletingUser(null); }}
        />
      )}
    </div>
  );
}

// ── My Profile ────────────────────────────────────────────────────────────────

export function MyProfile() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    job_title: '',
    department: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
  });

  useEffect(() => {
    authService.getUserProfile().then((p) => {
      setProfile(p);
      setForm({
        first_name: p.firstName || '',
        last_name: p.lastName || '',
        phone: p.phone || '',
        job_title: p.jobTitle || '',
        department: p.department || '',
        emergency_contact_name: p.emergencyContactName || '',
        emergency_contact_phone: p.emergencyContactPhone || '',
        emergency_contact_relationship: p.emergencyContactRelationship || '',
      });
    }).catch(() => {
      showError('Failed to load profile');
    }).finally(() => setIsLoading(false));
  }, []);

  const handleSave = async () => {
    if (!profile) return;
    setIsSaving(true);
    try {
      await userService.updateUserProfile(profile.id, {
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        job_title: form.job_title,
        department: form.department,
        emergency_contact_name: form.emergency_contact_name,
        emergency_contact_phone: form.emergency_contact_phone,
        emergency_contact_relationship: form.emergency_contact_relationship,
      });
      setProfile((p) => p ? {
        ...p,
        firstName: form.first_name,
        lastName: form.last_name,
        name: `${form.first_name} ${form.last_name}`,
        phone: form.phone,
        jobTitle: form.job_title,
        department: form.department,
        emergencyContactName: form.emergency_contact_name,
        emergencyContactPhone: form.emergency_contact_phone,
        emergencyContactRelationship: form.emergency_contact_relationship,
      } : p);
      setIsEditing(false);
      showSuccess('Profile updated');
    } catch {
      showError('Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  const set = (key: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [key]: v }));

  const downloadVCard = () => {
    if (!profile) return;
    const firstName = form.first_name || profile.firstName || '';
    const lastName = form.last_name || profile.lastName || '';
    const lines = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${firstName} ${lastName}`.trim(),
      `N:${lastName};${firstName};;;`,
      profile.email ? `EMAIL;TYPE=WORK:${profile.email}` : '',
      form.phone ? `TEL;TYPE=WORK,VOICE:${form.phone}` : '',
      form.job_title ? `TITLE:${form.job_title}` : '',
      form.department ? `ORG:;${form.department}` : '',
      'END:VCARD',
    ].filter(Boolean).join('\r\n');

    const blob = new Blob([lines], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${firstName}_${lastName}.vcf`.replace(/\s+/g, '_');
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
        Loading profile…
      </div>
    );
  }

  if (!profile) return null;

  const initials = `${profile.firstName?.[0] ?? ''}${profile.lastName?.[0] ?? ''}`.toUpperCase() || '?';

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xl font-bold shrink-0">
            {initials}
          </div>
          <div>
            <h1 className="text-xl font-semibold">{profile.name}</h1>
            <p className="text-sm text-muted-foreground">{profile.email}</p>
            <div className="flex gap-1 mt-1 flex-wrap">
              {profile.roles.map((r) => (
                <Badge key={r} variant="secondary" className="text-xs">{r}</Badge>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadVCard}
          >
            <Download className="h-4 w-4 mr-2" />
            Download vCard
          </Button>
          <Button
            variant={isEditing ? 'outline' : 'default'}
            size="sm"
            onClick={() => setIsEditing((v) => !v)}
          >
            <Edit className="h-4 w-4 mr-2" />
            {isEditing ? 'Cancel' : 'Edit Profile'}
          </Button>
        </div>
      </div>

      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <User className="h-4 w-4" />
            Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>First Name</Label>
            <Input
              value={form.first_name}
              onChange={(e) => set('first_name')(e.target.value)}
              readOnly={!isEditing}
              className={!isEditing ? 'bg-muted' : ''}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Last Name</Label>
            <Input
              value={form.last_name}
              onChange={(e) => set('last_name')(e.target.value)}
              readOnly={!isEditing}
              className={!isEditing ? 'bg-muted' : ''}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input value={profile.email} readOnly className="bg-muted" />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input
              value={form.phone}
              onChange={(e) => set('phone')(e.target.value)}
              readOnly={!isEditing}
              className={!isEditing ? 'bg-muted' : ''}
              placeholder="Not set"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Job Title</Label>
            <Input
              value={form.job_title}
              onChange={(e) => set('job_title')(e.target.value)}
              readOnly={!isEditing}
              className={!isEditing ? 'bg-muted' : ''}
              placeholder="Not set"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Department</Label>
            <Input
              value={form.department}
              onChange={(e) => set('department')(e.target.value)}
              readOnly={!isEditing}
              className={!isEditing ? 'bg-muted' : ''}
              placeholder="Not set"
            />
          </div>
        </CardContent>
      </Card>

      {/* Account Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-4 w-4" />
            Account Details
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Username</Label>
            <Input value={profile.username} readOnly className="bg-muted" />
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <div className="flex items-center h-9">
              <Badge variant={profile.isActive !== false ? 'default' : 'destructive'}>
                {profile.isActive !== false ? 'Active' : 'Inactive'}
              </Badge>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Email Verified</Label>
            <div className="flex items-center h-9">
              <Badge variant={profile.emailVerified ? 'default' : 'outline'}>
                {profile.emailVerified ? 'Verified' : 'Not verified'}
              </Badge>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Member Since</Label>
            <Input
              value={profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'Unknown'}
              readOnly
              className="bg-muted"
            />
          </div>
          {profile.lastLogin && (
            <div className="space-y-1.5">
              <Label>Last Login</Label>
              <Input
                value={new Date(profile.lastLogin).toLocaleString()}
                readOnly
                className="bg-muted"
              />
            </div>
          )}
          {profile.loginCount != null && (
            <div className="space-y-1.5">
              <Label>Total Logins</Label>
              <Input value={String(profile.loginCount)} readOnly className="bg-muted" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Phone className="h-4 w-4" />
            Emergency Contact
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              value={form.emergency_contact_name}
              onChange={(e) => set('emergency_contact_name')(e.target.value)}
              readOnly={!isEditing}
              className={!isEditing ? 'bg-muted' : ''}
              placeholder="Not set"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Phone</Label>
            <Input
              value={form.emergency_contact_phone}
              onChange={(e) => set('emergency_contact_phone')(e.target.value)}
              readOnly={!isEditing}
              className={!isEditing ? 'bg-muted' : ''}
              placeholder="Not set"
            />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Relationship</Label>
            <Input
              value={form.emergency_contact_relationship}
              onChange={(e) => set('emergency_contact_relationship')(e.target.value)}
              readOnly={!isEditing}
              className={!isEditing ? 'bg-muted' : ''}
              placeholder="e.g. Spouse, Parent"
            />
          </div>
        </CardContent>
      </Card>

      {/* Password & Security */}
      <PasswordSecurityCard email={profile.email} />

      {/* Save */}
      {isEditing && (
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <RefreshCw className="h-4 w-4 animate-spin mr-2" />}
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
}

function PasswordSecurityCard({ email }: { email: string }) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSendResetLink = async () => {
    setSending(true);
    setError('');
    try {
      await authService.forgotPassword(email);
      setSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset link. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4" />
          Password &amp; Security
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {sent ? (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              A password reset link has been sent to <strong>{email}</strong>. Check your inbox and follow the link to set a new password.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              To change your password, we'll send a reset link to your account email address.
            </p>
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <Button variant="outline" onClick={handleSendResetLink} disabled={sending}>
              {sending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</>
              ) : (
                <><Mail className="mr-2 h-4 w-4" />Send Password Reset Link</>
              )}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}